export type UnhandledError = {
  detail: string;
};

let currentError: UnhandledError | null = null;
const listeners = new Set<() => void>();

const IGNORED_MESSAGE_PREFIXES = ['ResizeObserver loop'];

function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function subscribeToUnhandledErrors(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUnhandledError(): UnhandledError | null {
  return currentError;
}

export function reportUnhandledError(error: unknown): void {
  const detail = describeError(error);

  if (detail === '' || IGNORED_MESSAGE_PREFIXES.some((prefix) => detail.startsWith(prefix))) {
    return;
  }

  if (currentError?.detail === detail) {
    return;
  }

  currentError = { detail };
  listeners.forEach((listener) => listener());
}

export function dismissUnhandledError(): void {
  if (currentError === null) {
    return;
  }

  currentError = null;
  listeners.forEach((listener) => listener());
}

export function installUnhandledErrorReporting(): () => void {
  const onError = (event: ErrorEvent) => reportUnhandledError(event.error ?? event.message);
  const onUnhandledRejection = (event: PromiseRejectionEvent) => reportUnhandledError(event.reason);

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}
